import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import '../UsageAnalyticsPage/UsageAnalyticsPage.css';
import './TasksPage.css';
import { describeSchedule } from '../../../i18n/format';
import { useLocale } from '../../../i18n/useLocale';
import { fetchTaskMetrics, TaskMetrics } from '../../client/UsageAnalyticsClient';
import { loadErrorText, toLoadError, type LoadError } from '../../utils/loadError';
import { evalAgentLabel } from "../../agents";
import AskEvalAgent from '../../components/AskEvalAgent/AskEvalAgent';

/** The page's agent and date range, named in every ask-the-agent question. */
// A type alias, not an interface: i18next's options need an index signature.
type AskScope = {
  agent: string;
  start: string;
  end: string;
};

// ── Constants ────────────────────────────────────────────────────────────────

// Agent selection lives in EvalLayout's sidebar and arrives via
// <Outlet context={{ agent }} />; the Task metrics API isn't agent-scoped, so
// this is only used for the page title.

interface EvalOutletContext {
  agent: string;
}

/** YYYY-MM-DD `days` ago. */
function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Whole days between two YYYY-MM-DD dates (for chart windowing), min 1. */
function daysBetween(start: string, end: string): number {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return 7;
  return Math.max(1, Math.round((e - s) / 86_400_000));
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return n.toLocaleString('en-US');
  return String(n);
}

function formatLastActive(isoStr: string, dateLocale: string): string {
  if (!isoStr) return '—';
  try {
    const date = new Date(isoStr);
    return date.toLocaleString(dateLocale, {
      timeZone: 'Asia/Shanghai',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return isoStr;
  }
}

function getStatusBadge(status: string): { symbol: string; className: string } {
  const upper = status.toUpperCase();
  if (upper === 'SUCCESS') return { symbol: '✓', className: 'task-status-badge task-status-badge--success' };
  if (upper === 'FAILED') return { symbol: '✗', className: 'task-status-badge task-status-badge--failed' };
  return { symbol: '—', className: 'task-status-badge task-status-badge--never' };
}

const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/?uid=';
const PHONETOOL_URL = 'https://phonetool.amazon.com/users/';

function UserAvatar({ login }: { login: string }) {
  const [failed, setFailed] = useState(false);
  if (!login) return <span>—</span>;
  return (
    <a className="user-avatar" href={`${PHONETOOL_URL}${login}`} target="_blank" rel="noopener">
      {!failed ? (
        <img
          className="user-avatar__img"
          src={`${BADGE_PHOTO_URL}${encodeURIComponent(login)}`}
          alt=""
          referrerPolicy="origin"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="user-avatar__fallback">{login.charAt(0).toUpperCase()}</span>
      )}
      {login}
    </a>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function TasksPage() {
  const { t } = useTranslation('eval');
  const { agent } = useOutletContext<EvalOutletContext>();
  // Concrete date range (default: last 7 days), mirroring Usage / Reports.
  const [startDate, setStartDate] = useState<string>(() => daysAgo(7));
  const [endDate, setEndDate] = useState<string>(() => todayIso());

  const [metrics, setMetrics] = useState<TaskMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoadError | null>(null);

  const agentLabel = evalAgentLabel(agent);

  const doFetch = useCallback(async (fetchStart: string, fetchEnd: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTaskMetrics('custom', fetchStart, fetchEnd, agent);
      setMetrics(data);
    } catch (err: unknown) {
      setError(toLoadError(err, 'tasks.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [agent]);

  useEffect(() => {
    if (!startDate || !endDate || endDate < startDate) return;
    doFetch(startDate, endDate);
  }, [startDate, endDate, doFetch]);

  const rangeDays = daysBetween(startDate, endDate);

  return (
    <div className="eval-usage">
      {/* Header — matches Usage / Reports: title + last-update + date range */}
      <div className="eval-usage__header">
        <div className="eval-usage__header-left">
          <h1 className="eval-usage__page-title">{t('tasks.title', { agent: agentLabel })}</h1>
          <p className="eval-usage__page-subtitle">{t('shared.lastUpdate', { time: new Date().toLocaleString(t('shared.updatedLocale') || undefined) })}</p>
        </div>
        <div className="eval-usage__date-picker">
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label={t('shared.startDate')}
          />
          <span>—</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayIso()}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label={t('shared.endDate')}
          />
        </div>
      </div>

      {/* Content */}
      {loading && <div className="eval-usage__loading">{t('tasks.loading')}</div>}
      {error && <div className="eval-usage__error">{loadErrorText(error, t)}</div>}
      {!loading && !error && metrics && (
        <TasksContent metrics={metrics} rangeDays={rangeDays} ask={{ agent: agentLabel, start: startDate, end: endDate }} />
      )}
      {!loading && !error && !metrics && <div className="eval-usage__loading">{t('shared.noData')}</div>}
    </div>
  );
}

// ── Content Section ──────────────────────────────────────────────────────────

function TasksContent({ metrics, rangeDays, ask }: { metrics: TaskMetrics; rangeDays: number; ask: AskScope }) {
  const { t } = useTranslation('eval');
  const { summary, dailyExecutions, typeDistribution, durationDistribution, tasks, topFailed } = metrics;

  const kpis = [
    { label: t('tasks.activeTasks'), value: formatNumber(summary.activeTasks) },
    { label: t('tasks.executions'), value: formatNumber(summary.totalExecutions) },
    { label: t('tasks.successRate'), value: `${summary.successRate.toFixed(1)}%`, className: 'task-rate--green' },
    { label: t('tasks.failed'), value: formatNumber(summary.failedCount) },
  ];

  // Limit chart bars to the selected range.
  const displayDaily = dailyExecutions.slice(-rangeDays);
  const maxDaily = Math.max(...displayDaily.map((d) => d.success + d.failed), 1);

  return (
    <>
      {/* Overview: Usage card (left) | Daily Executions chart (right) */}
      <div className="task-overview">
        <div className="eval-usage__kpi-card task-usage-card">
          <h3 className="eval-usage__section-title">
            {t('tasks.section')}
            <AskEvalAgent
              question={t('askAgent.tasks.overview', {
                ...ask,
                active: formatNumber(summary.activeTasks),
                executions: formatNumber(summary.totalExecutions),
                rate: `${summary.successRate.toFixed(1)}%`,
                failed: formatNumber(summary.failedCount),
              })}
            />
          </h3>
          <div className="task-usage-grid">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="task-usage-stat">
                <p className="eval-usage__kpi-label">{kpi.label}</p>
                <p className={`eval-usage__kpi-value ${kpi.className ?? ''}`}>{kpi.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="eval-usage__chart-card task-chart-card">
          <div className="task-chart-head">
            <h3 className="eval-usage__section-title">
              {t('tasks.dailyExecutions')}
              <AskEvalAgent question={t('askAgent.tasks.daily', ask)} />
            </h3>
            <div className="task-chart-legend">
              <span className="task-chart-legend__item">
                <span className="task-chart-legend__dot task-chart-legend__dot--success" />
                {t('tasks.success')}
              </span>
              <span className="task-chart-legend__item">
                <span className="task-chart-legend__dot task-chart-legend__dot--failed" />
                {t('tasks.failed')}
              </span>
            </div>
          </div>
          <div className="eval-usage__chart-wrapper">
            <div className="eval-usage__chart-yaxis">
              <span>{formatNumber(maxDaily)}</span>
              <span>{formatNumber(Math.round(maxDaily / 2))}</span>
              <span>0</span>
            </div>
            <div className="eval-usage__chart-container">
              {displayDaily.map((day) => {
                const total = day.success + day.failed;
                const heightPct = (total / maxDaily) * 100;
                const successPct = total > 0 ? (day.success / total) * 100 : 0;
                const failedPct = total > 0 ? (day.failed / total) * 100 : 0;
                return (
                  <div key={day.date} className="eval-usage__chart-bar-wrapper">
                    <div
                      className="task-bar-chart__bar"
                      style={{ height: `${heightPct}%` }}
                      title={t('tasks.dayBar', { date: day.date, success: day.success, failed: day.failed })}
                    >
                      <div
                        className="task-bar-chart__segment--failed"
                        style={{ height: `${failedPct}%` }}
                      />
                      <div
                        className="task-bar-chart__segment--success"
                        style={{ height: `${successPct}%` }}
                      />
                    </div>
                    <span className="eval-usage__chart-label">{day.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Two-Column: Type Distribution + Duration Distribution */}
      <div className="eval-usage__two-col">
        <div className="eval-usage__table-card">
          <h3 className="eval-usage__section-title">
            {t('tasks.typeDistribution')}
            <AskEvalAgent question={t('askAgent.tasks.types', ask)} />
          </h3>
          <table className="eval-usage__table">
            <thead>
              <tr>
                <th>{t('tasks.colType')}</th>
                <th>{t('tasks.colCount')}</th>
              </tr>
            </thead>
            <tbody>
              {typeDistribution.map((type) => (
                <tr key={type.taskType}>
                  <td>{type.taskType}</td>
                  <td className="eval-usage__table-num">{formatNumber(type.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="eval-usage__table-card">
          <h3 className="eval-usage__section-title">
            {t('tasks.durationDistribution')}
            <AskEvalAgent question={t('askAgent.tasks.durations', ask)} />
          </h3>
          <table className="eval-usage__table">
            <thead>
              <tr>
                <th>{t('tasks.colBucket')}</th>
                <th>{t('tasks.colCount')}</th>
              </tr>
            </thead>
            <tbody>
              {durationDistribution.map((d) => (
                <tr key={d.bucket}>
                  <td>{d.bucket}</td>
                  <td className="eval-usage__table-num">{formatNumber(d.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom tabs: Top Failed Tasks | All Tasks */}
      <TaskTables tasks={tasks} topFailed={topFailed} rangeDays={rangeDays} ask={ask} />
    </>
  );
}

// ── Bottom tabs (Top Failed | All Tasks) ──────────────────────────────────────

function TaskTables({
  tasks,
  topFailed,
  rangeDays,
  ask,
}: {
  tasks: TaskMetrics['tasks'];
  topFailed: TaskMetrics['topFailed'];
  rangeDays: number;
  ask: AskScope;
}) {
  const { t } = useTranslation('eval');
  const { locale } = useLocale();
  const [tab, setTab] = useState<'failed' | 'all'>('failed');
  const [taskPage, setTaskPage] = useState(0);
  const [query, setQuery] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const PAGE_SIZE = 20;

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setTaskPage(0);
  };

  const filteredTasks = tasks.filter((task) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (task.name ?? '').toLowerCase().includes(q) ||
      (task.owner ?? '').toLowerCase().includes(q) ||
      (task.taskType ?? '').toLowerCase().includes(q)
    );
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let cmp = 0;
    const field = sortField as keyof typeof a;
    const av = a[field] ?? '';
    const bv = b[field] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sortedTasks.length / PAGE_SIZE);
  const pagedTasks = sortedTasks.slice(taskPage * PAGE_SIZE, (taskPage + 1) * PAGE_SIZE);
  const sortIcon = (field: string) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredFailed = topFailed.filter((f) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (f.name ?? '').toLowerCase().includes(q) || (f.owner ?? '').toLowerCase().includes(q);
  });

  return (
    <div className="task-tables">
      <div className="task-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'failed'}
          className={'task-tab' + (tab === 'failed' ? ' task-tab--active' : '')}
          onClick={() => { setTab('failed'); setQuery(''); }}
        >
          {t('tasks.topFailed', { n: topFailed.length })}
        </button>
        <button
          role="tab"
          aria-selected={tab === 'all'}
          className={'task-tab' + (tab === 'all' ? ' task-tab--active' : '')}
          onClick={() => { setTab('all'); setQuery(''); setTaskPage(0); }}
        >
          {t('tasks.allTasks', { n: tasks.length })}
        </button>
      </div>

      <div className="eval-usage__full-card">
        <div className="task-table-toolbar">
          <h3 className="eval-usage__section-title">
            {tab === 'failed' ? t('tasks.topFailed', { n: topFailed.length }) : t('tasks.allTasks', { n: tasks.length })}
            <AskEvalAgent question={t(tab === 'failed' ? 'askAgent.tasks.failedList' : 'askAgent.tasks.allList', ask)} />
          </h3>
          <div className="eval-usage__search-box">
            <svg className="eval-usage__search-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              className="eval-usage__search-input"
              placeholder={t('shared.findResources')}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setTaskPage(0); }}
              aria-label={t('shared.findResources')}
            />
          </div>
        </div>

        {tab === 'failed' ? (
          filteredFailed.length === 0 ? (
            <p className="eval-usage__loading">{t('tasks.noFailing')}</p>
          ) : (
            <table className="eval-usage__table tasks-table--failed">
              <thead>
                <tr>
                  <th>{t('tasks.colName')}</th>
                  <th>{t('tasks.colOwner')}</th>
                  <th>{t('tasks.colFailures')}</th>
                  <th>{t('tasks.colLastError')}</th>
                  <th>{t('tasks.colLastFailure')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredFailed.map((f) => (
                  <tr key={f.taskId}>
                    <td>
                      {f.name}
                      <AskEvalAgent
                        question={t('askAgent.tasks.failedItem', {
                          ...ask,
                          name: f.name,
                          owner: f.owner || '—',
                          failures: f.failures,
                          error: f.lastError || '—',
                        })}
                      />
                    </td>
                    <td><UserAvatar login={f.owner} /></td>
                    <td className="eval-usage__table-num">{f.failures}</td>
                    <td><span className="task-error-text">{f.lastError}</span></td>
                    <td>{formatLastActive(f.lastFailureTime, t('shared.dateLocale'))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <>
            <table className="eval-usage__table tasks-table--all">
              <thead>
                <tr>
                  <th className="task-sortable" onClick={() => toggleSort('name')}>{t('tasks.colName')}{sortIcon('name')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('owner')}>{t('tasks.colOwner')}{sortIcon('owner')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('creator')}>{t('tasks.colCreator')}{sortIcon('creator')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('taskType')}>{t('tasks.colType')}{sortIcon('taskType')}</th>
                  <th>{t('tasks.colSchedule')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('lastStatus')}>{t('tasks.colLastStatus')}{sortIcon('lastStatus')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('lastExecutionTime')}>{t('tasks.colLastExecution')}{sortIcon('lastExecutionTime')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('createdAt')}>{t('tasks.colCreated')}{sortIcon('createdAt')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('executionCount')}>{t('tasks.colExecs', { n: rangeDays })}{sortIcon('executionCount')}</th>
                  <th className="task-sortable" onClick={() => toggleSort('failureCount')}>{t('tasks.colFails', { n: rangeDays })}{sortIcon('failureCount')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedTasks.map((task) => {
                  const badge = getStatusBadge(task.lastStatus);
                  return (
                    <tr key={task.taskId}>
                      <td>
                        {task.name}
                        <AskEvalAgent
                          question={t('askAgent.tasks.item', {
                            ...ask,
                            name: task.name,
                            type: task.taskType || '—',
                            owner: task.owner || '—',
                            status: task.lastStatus || '—',
                            executions: formatNumber(task.executionCount),
                            failures: formatNumber(task.failureCount),
                          })}
                        />
                      </td>
                      <td><UserAvatar login={task.owner} /></td>
                      <td><UserAvatar login={task.creator} /></td>
                      <td>{task.taskType}</td>
                      <td>{describeSchedule(task.schedule, locale)}</td>
                      <td><span className={badge.className}>{badge.symbol} {task.lastStatus}</span></td>
                      <td>{formatLastActive(task.lastExecutionTime, t('shared.dateLocale'))}</td>
                      <td>{formatLastActive(task.createdAt, t('shared.dateLocale'))}</td>
                      <td className="eval-usage__table-num">{formatNumber(task.executionCount)}</td>
                      <td className="eval-usage__table-num">{task.failureCount > 0 ? formatNumber(task.failureCount) : '0'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="eval-usage__pagination">
                <button disabled={taskPage === 0} onClick={() => setTaskPage(taskPage - 1)}>{t('shared.prev')}</button>
                <span>{t('shared.page', { page: taskPage + 1, total: totalPages })}</span>
                <button disabled={taskPage >= totalPages - 1} onClick={() => setTaskPage(taskPage + 1)}>{t('shared.next')}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
